import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { ActionGetPatientDischargeForm } from '../../../store/Actions/AppConfigAction';
import { ActionUpdateTreatmentSlot } from '../../../store/Actions/TreatmentSlotAction';
import { message } from 'antd';
import { useFetchOnce } from '../../../hooks';
import { useSlotIds } from '../../../hooks/useSlotIds';
import { ActionUpdateServiceSlot } from '../../../store/Actions/ServiceSlotAction';
import { ActionUpdateSolutionSlot } from '../../../store/Actions/SolutionSlotAction';
import { ActionUpdateProgrammeSlot } from '../../../store/Actions/ServiceAction';

/**
 * Custom hook for managing discharge form data and state
 * @param {string} patientPlanId - The patient plan ID from URL params
 * @returns {Object} Form data, state, and handlers
 * 
 * NOTE: This hook uses useFetchOnce to prevent duplicate API calls during re-renders
 */
export const useDischargeForm = () => {
    const {
        activeSlotId,
        patientPlanId,
        activeSlotName
    } = useSlotIds();
    const dispatch = useDispatch();
    const { patientDischarge } = useSelector(state => state?.appConfig);

    // Comprehensive logging for debugging
    console.log('patientPlanId:', patientPlanId);
    console.log('patientDischarge:', patientDischarge);

    // Validate data structure
    if (patientDischarge && patientDischarge.length > 0) {
        const data = patientDischarge[0];
    } else {
        console.warn('⚠️ No patient discharge data available');
    }

    const [isEditMode, setIsEditMode] = useState(false);

    // Fetch patient discharge data using useFetchOnce hook
    const { loading, error } = useFetchOnce({
        action: ActionGetPatientDischargeForm,
        actionParams: { Query: `/${patientPlanId}` },
        dependency: patientPlanId,
        enabled: !!patientPlanId,
        onSuccess: (response) => {
            console.log('Response:', response);
        },
        onError: (error) => {
            console.error('❌ Failed to fetch patient discharge data');
            console.error('Error:', error);
        },
    });

    // Transform services data for table
    const getServicesTableData = () => {
        try {
            const response = patientDischarge?.[0]?.serviceDetails;
            if (!response || typeof response !== 'object') {
                console.warn('⚠️ No services data available or invalid format');
                return [];
            }

            const aggregated = {};

            const formatToAMPM = (timeStr) => {
                if (!timeStr || typeof timeStr !== 'string' || timeStr === '—') return '—';
                // If already has AM/PM, return as is
                if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;

                try {
                    return dayjs(`2000-01-01 ${timeStr}`).format('h:mm A');
                } catch (e) {
                    return timeStr;
                }
            };

            Object.keys(response).forEach(date => {
                const dayServices = response[date];
                if (!Array.isArray(dayServices)) return;

                dayServices.forEach(service => {
                    const displayStartTime = formatToAMPM(service.startTime);
                    const displayEndTime = formatToAMPM(service.endTime);

                    const timeKey = service.startTime && service.endTime
                        ? `${service.startTime}-${service.endTime}`
                        : (service.startTime || 'unscheduled');
                    const groupKey = `${service.serviceName}|${timeKey}`;

                    if (!aggregated[groupKey]) {
                        aggregated[groupKey] = {
                            serviceName: service.serviceName || 'N/A',
                            time: service.startTime && service.endTime
                                ? `${displayStartTime} to ${displayEndTime}`
                                : displayStartTime,
                            startTime: service.startTime || '99:99',
                            medicineLogs: [], // Array of {date, medicine}
                            holdLogs: [], // Array of {date, status}
                            takenDates: new Set(),
                            holdDates: new Set(),
                        };
                    }

                    // Determine if the record is a "Hold" or "Not Taken" status
                    const rawMedValue = (service.medicineValue || '').trim();
                    if (!rawMedValue) return; // Skip if no medicine/status recorded

                    const medValueLower = rawMedValue.toLowerCase();
                    const isHold = medValueLower.includes('hold') ||
                        medValueLower.includes('not taken') ||
                        medValueLower === 'none' ||
                        medValueLower === 'no' ||
                        medValueLower === 'n/a' ||
                        medValueLower === 'na';

                    if (isHold) {
                        aggregated[groupKey].holdDates.add(date);
                        aggregated[groupKey].holdLogs.push({
                            date: dayjs(date).format('DD MMM'),
                            status: rawMedValue || 'Hold'
                        });
                    } else {
                        aggregated[groupKey].takenDates.add(date);
                        if (rawMedValue) {
                            aggregated[groupKey].medicineLogs.push({
                                date: dayjs(date).format('DD MMM'),
                                medicine: rawMedValue
                            });
                        }
                    }
                });
            });

            return Object.values(aggregated).map(item => {
                const allDates = new Set([...item.takenDates, ...item.holdDates]);
                const totalDays = allDates.size > 0 ? allDates.size - 1 : 0;

                // Process medications and hold statuses with dates
                let medicineDisplay = '—';
                const medicineSections = [];

                // Helper to group consecutive dates into ranges
                const groupDatesIntoRanges = (logs, propertyName) => {
                    const groups = {};
                    logs.forEach(log => {
                        const key = log[propertyName];
                        if (!groups[key]) groups[key] = [];
                        groups[key].push(log.date);
                    });

                    return Object.keys(groups).map(key => {
                        const dates = groups[key];
                        // Sort dates ensuring handle year transitions if any (though here we only have DD MMM)
                        const sorted = [...dates].sort((a, b) => {
                            const da = dayjs(a, 'DD MMM');
                            const db = dayjs(b, 'DD MMM');
                            return da.diff(db);
                        });

                        const ranges = [];
                        let rangeStart = sorted[0];
                        let rangeLast = sorted[0];

                        for (let i = 1; i <= sorted.length; i++) {
                            const current = sorted[i];
                            const isConsecutive = current && dayjs(current, 'DD MMM').diff(dayjs(rangeLast, 'DD MMM'), 'day') === 1;

                            if (!isConsecutive) {
                                if (rangeStart === rangeLast) {
                                    ranges.push(rangeStart);
                                } else {
                                    ranges.push(`${rangeStart} to ${rangeLast}`);
                                }
                                rangeStart = current;
                            }
                            rangeLast = current;
                        }

                        return dates.length > 0 ? `${key} (${ranges.join(', ')})` : key;
                    });
                };

                // 1. Process regular medicines
                if (item.medicineLogs.length > 0) {
                    medicineSections.push(...groupDatesIntoRanges(item.medicineLogs, 'medicine'));
                }

                // 2. Process hold/not taken statuses with specific dates
                let holdDisplay = '';
                if (item.holdLogs.length > 0) {
                    holdDisplay = groupDatesIntoRanges(item.holdLogs, 'status').join('; ');
                }

                // 3. Combine for display
                if (medicineSections.length > 0) {
                    medicineDisplay = medicineSections.join(', ');
                    if (holdDisplay) {
                        medicineDisplay += ` [Note: ${holdDisplay}]`;
                    }
                } else if (holdDisplay) {
                    medicineDisplay = holdDisplay;
                }

                return {
                    key: `${item.serviceName}-${item.time}`,
                    serviceName: item.serviceName,
                    time: item.time,
                    startTime: item.startTime,
                    medicine: medicineDisplay,
                    days: totalDays
                };
            }).sort((a, b) => {
                const timeA = dayjs(`2000-01-01 ${a.startTime.includes(':') ? a.startTime : '00:00'}`);
                const timeB = dayjs(`2000-01-01 ${b.startTime.includes(':') ? b.startTime : '00:00'}`);
                return timeA.isAfter(timeB) ? 1 : -1;
            });

        } catch (error) {
            console.error('❌ Error processing services table data:', error);
            return [];
        }
    };

    // Get initial form values
    const getInitialValues = () => {
        try {
            const data = patientDischarge?.[0];

            if (!data) {
                console.warn('⚠️ No patient data available for initial values');
                return {
                    customerName: '',
                    customerId: '',
                    email: '',
                    age: '',
                    birthDate: null,
                    gender: '',
                    dischargeType: '',
                    startDate: null,
                    endDate: null,
                    totalDays: '',
                    diagnosis: '',
                    hetu: '',
                    addiction: '',
                    admissionComplaints: '',
                    dischargeComplaints: '',
                    previousHistory: '',
                    investigationBefore: '',
                    investigationAdmit: '',
                    investigationsAdvised: '',
                    medicineAdvised: '',
                    virechana: '',
                    Exercise: '',
                    diet: '',
                    instructionMentalPeace: '',
                    nextPanchakramaAdvise: '',
                    admissionTime: null,
                    dischargeTime: null,
                };
            }

            // Calculate age from birthDate
            let calculatedAge = '';
            let parsedBirthDate = null;

            if (data?.customerId?.birthDate) {
                try {
                    // Try parsing ISO format first
                    parsedBirthDate = dayjs(data.customerId.birthDate);

                    // If invalid, try DD-MM-YYYY format
                    if (!parsedBirthDate.isValid()) {
                        parsedBirthDate = dayjs(data.customerId.birthDate, 'DD-MM-YYYY');
                    }

                    if (parsedBirthDate.isValid()) {
                        calculatedAge = dayjs().diff(parsedBirthDate, 'year');
                    } else {
                        console.warn('⚠️ Invalid birthDate format:', data.customerId.birthDate);
                    }
                } catch (error) {
                    console.error('❌ Error parsing birthDate:', error);
                }
            }

            // Parse start and end dates
            const parseDate = (dateStr, source) => {
                if (!dateStr) return null;

                try {
                    // Try DD-MM-YYYY format first (API format)
                    let parsed = dayjs(dateStr, 'DD-MM-YYYY');

                    // If invalid, try ISO format
                    if (!parsed.isValid()) {
                        parsed = dayjs(dateStr);
                    }

                    if (parsed.isValid()) {
                        return parsed;
                    } else {
                        console.warn(`⚠️ Invalid ${source} date:`, dateStr);
                        return null;
                    }
                } catch (error) {
                    console.error(`❌ Error parsing ${source} date:`, error);
                    return null;
                }
            };

            // Extract saved discharge form values from slot data
            const slotData = data?.solutionSlotId ?? data?.activeSlotId ?? data?.treatmentSlotId ?? data?.programSlotId ?? null;
            const savedForm = slotData?.dischargeForm || {};

            const startDate = parseDate(
                slotData?.startDate || data?.confirmationDetails?.arrivingFrom,
                'start'
            );

            const endDate = parseDate(
                slotData?.endDate,
                'end'
            );

            // Helper to get value from personalDetails array
            const getPersonalDetail = (key) => {
                return data?.personalDetails?.find(item => item.personalDetilsKey === key)?.value || '';
            };

            // Handle admission and discharge times consistently
            const formatTimeFromSource = (dateSource, fallback) => {
                if (!dateSource) return fallback || '';
                const parsed = dayjs(dateSource);
                return parsed.isValid() ? parsed.format('h:mm a') : (fallback || '');
            };

            const initialValues = {
                // Patient Basic Info
                customerName: data?.customerName || '',
                treatmentName: data?.treatmentName || '',
                customerId: data?.customerId?._id || '',
                email: data?.customerId?.email || '',
                age: calculatedAge,
                birthDate: parsedBirthDate?.isValid() ? parsedBirthDate : null,
                gender: data?.customerGender || '',

                // Admission Details
                dischargeType: savedForm?.dischargeType || data?.dischargeType || '',
                startDate,
                endDate,
                totalDays: (startDate && endDate)
                    ? dayjs(endDate).diff(dayjs(startDate), 'day')
                    : (slotData?.totalDays || ''),

                // Medical Information
                diagnosis: savedForm?.diagnosis || data?.diagnosis || data?.treatmentName || '',
                hetu: savedForm?.hetu || data?.hetu || '',
                addiction: savedForm?.addiction || data?.addiction || '',
                admissionComplaints: savedForm?.admissionComplaints || data?.admissionComplaints || getPersonalDetail('ChiefComplaints') || '',
                dischargeComplaints: savedForm?.dischargeComplaints || data?.dischargeComplaints || '',
                previousHistory: savedForm?.previousHistory || data?.previousHistory || getPersonalDetail('past_surgical_history') || getPersonalDetail('HistoryOfAllergies') || '',

                // Investigations
                investigationBefore: savedForm?.investigationBefore || data?.investigationBefore || '',
                investigationAdmit: savedForm?.investigationAdmit || data?.investigationAdmit || '',
                investigationsAdvised: savedForm?.investigationsAdvised || savedForm?.investigationsAdviced || data?.investigationsAdvised || data?.investigationsAdviced || '',

                // Discharge Advice
                medicineAdvised: savedForm?.medicineAdvised || data?.medicineAdvised || '',
                virechana: savedForm?.virechana || savedForm?.Virechana || data?.virechana || data?.Virechana || '',
                Exercise: savedForm?.Exercise || data?.Exercise || '',
                diet: savedForm?.diet || savedForm?.Diet || data?.diet || data?.Diet || '',
                instructionMentalPeace: savedForm?.instructionMentalPeace || data?.instructionMentalPeace || '',
                nextPanchakramaAdvise: savedForm?.nextPanchakramaAdvise || savedForm?.nextPanchakrmaAdvise || data?.nextPanchakramaAdvise || data?.nextPanchakrmaAdvise || '',
                admissionTime: formatTimeFromSource(slotData?.actualCheckInDate, data?.admissionTime),
                dischargeTime: formatTimeFromSource(slotData?.actualCheckOutDate, data?.dischargeTime),
            };

            // --- DEBUG: Prefill Validation ---
            const emptyFields = Object.entries(initialValues)
                .filter(([_, value]) => value === '' || value === null || value === undefined)
                .map(([key]) => key);
            if (emptyFields.length > 0) {
                console.warn('⚠️ The following fields failed to prefill (are empty/null):', emptyFields);
            } else {
                console.log('✅ All fields prefilled successfully!');
            }

            return initialValues;
        } catch (error) {
            console.error('❌ Error in getInitialValues:', error);
            return {};
        }
    };

    // Handle form submission
    const handleFormSubmit = (values, { setSubmitting }) => {
        try {
            // Validate required IDs
            if (!activeSlotId) {
                console.error('❌ Missing activeSlotId');
                message.error('Treatment slot ID is missing. Cannot submit form.');
                setSubmitting(false);
                return;
            }

            if (!values.dischargeType) {
                message.warning('Please select a Discharge Type');
                setSubmitting(false);
                return;
            }

            // Destructure to separate display-only fields from saveable data
            const {
                birthDate, customerName, email, age, gender, treatmentName, customerId,
                startDate, endDate, totalDays, admissionTime, dischargeTime,
                ...dischargeFormData
            } = values;

            // Proper mutation: Prepare the final payload
            const payload = {
                // Update slot-level fields if they changed
                startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
                endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
                totalDays: totalDays || undefined,

                // Nest discharge form details
                dischargeForm: {
                    ...dischargeFormData,
                    admissionTime: admissionTime ? (dayjs.isDayjs(admissionTime) ? admissionTime.format('h:mm a') : admissionTime) : undefined,
                    dischargeTime: dischargeTime ? (dayjs.isDayjs(dischargeTime) ? dischargeTime.format('h:mm a') : dischargeTime) : undefined,
                }
            };

            const ActiveUpdateSlot = activeSlotName === 'service' ? ActionUpdateServiceSlot :
                activeSlotName === 'solution' ? ActionUpdateSolutionSlot :
                    activeSlotName === 'program' ? ActionUpdateProgrammeSlot :
                        ActionUpdateTreatmentSlot;

            dispatch(
                ActiveUpdateSlot(
                    {
                        Query: activeSlotId,
                        obj: payload,
                    },
                    (response) => {
                        setSubmitting(false);
                        if (response?.statusCode === 200) {
                            message.success('Discharge form updated successfully');
                            setIsEditMode(false);
                        } else {
                            console.error('❌ Failed to update:', response?.message);
                            message.error(response?.message || 'Failed to update discharge form');
                        }
                    }
                )
            );
        } catch (error) {
            console.error('❌ Error in form submission:', error);
            message.error('An error occurred while submitting the form');
            setSubmitting(false);
        }
    };

    // Toggle edit mode
    const toggleEditMode = () => {
        setIsEditMode(prev => !prev);
    };

    const initialValues = getInitialValues();

    return {
        patientDischarge,
        loading,
        error,
        isEditMode,
        initialValues,
        servicesTableData: getServicesTableData(),
        toggleEditMode,
        handleFormSubmit,
    };
};
